import React from "react";
import { SettingsOutlined } from "@material-ui/icons";
import { Button } from "@my-scoot/component-library-legacy";
import withComponentLibraryTheme from "hocs/withComponentLibraryTheme";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { OffersSurgeSettingsModal } from "./OffersSurgeSettingsModal";
import { useOffersSurgeSettingsStore } from "../store/useOffersSurgeSettingsStore";
import {
  mapSelectionMethod,
  mapSelectionMethodFromBackend,
} from "../utils/offerContract";
import { useOfferSettings, useSaveOfferSettings } from "../useOfferApis";

// File-local copy: no shared display-strings map exists for these notify
// strings (other offer notifies are also inline), so keep it local.
const SETTINGS_SAVED_MESSAGE = "Pricing settings saved";

/**
 * Account-level entry point (§4.7 + §12): the same modal/store, seeded from
 * the account settings (no listing_uid) and saved without one. Mounts beside
 * the Create button on the offers list page — the account kill switch.
 */
export const OffersAccountSettingsButton = withComponentLibraryTheme(() => {
  const { notify } = useNotifications();
  const setOpen = useOffersSurgeSettingsStore((s) => s.setOpen);
  const setMasterEnabled = useOffersSurgeSettingsStore(
    (s) => s.setMasterEnabled
  );
  const setSelectionMethod = useOffersSurgeSettingsStore(
    (s) => s.setSelectionMethod
  );

  const { data: serverSettings } = useOfferSettings("", true);
  const { mutate: saveSettings, isLoading: isSaving } = useSaveOfferSettings({
    onSuccess: () =>
      notify(SETTINGS_SAVED_MESSAGE, notification_color_keys.success),
  });

  const seeded = React.useRef(false);
  React.useEffect(() => {
    if (!serverSettings || seeded.current) return;
    seeded.current = true;
    if (typeof serverSettings.is_enabled === "boolean") {
      setMasterEnabled(serverSettings.is_enabled);
    }
    if (serverSettings.selection_method) {
      setSelectionMethod(
        mapSelectionMethodFromBackend(serverSettings.selection_method)
      );
    }
  }, [serverSettings, setMasterEnabled, setSelectionMethod]);

  const handleSave = ({ masterEnabled, selectionMethod }) => {
    saveSettings({
      is_enabled: !!masterEnabled,
      selection_method: mapSelectionMethod(selectionMethod),
    });
  };

  return (
    <div className="tw-flex tw-items-stretch tw-h-full">
      <Button
        color="secondary"
        variant="outlined"
        size="small"
        className="!tw-h-full tw-w-full !tw-text-[14px]"
        onClick={() => setOpen(true, null)}
      >
        <div className="tw-h-[38px] tw-flex tw-justify-center tw-items-center">
          <SettingsOutlined className="!tw-h-[18px] !tw-w-[18px] tw-mr-[6px]" />
          Pricing Settings
        </div>
      </Button>
      <OffersSurgeSettingsModal onSave={handleSave} saving={isSaving} />
    </div>
  );
});
/**
 * Per-offering entry point for Offers & Surge settings (§4.7).
 * Sits next to "Manage variations" — same access pattern.
 * Loads/saves `host/offer/settings` for the offering (listing_uid).
 */
export const OffersSurgeSettingsButton = withComponentLibraryTheme(
  ({ offeringUuid }) => {
    const { notify } = useNotifications();
    const setOpen = useOffersSurgeSettingsStore((s) => s.setOpen);
    const setMasterEnabled = useOffersSurgeSettingsStore(
      (s) => s.setMasterEnabled
    );
    const setSelectionMethod = useOffersSurgeSettingsStore(
      (s) => s.setSelectionMethod
    );

    const { data: serverSettings } = useOfferSettings(
      offeringUuid || "",
      !!offeringUuid
    );
    const { mutate: saveSettings, isLoading: isSaving } = useSaveOfferSettings({
      onSuccess: () =>
        notify(SETTINGS_SAVED_MESSAGE, notification_color_keys.success),
    });

    // Seed the modal from the server once it arrives.
    const seededFor = React.useRef(null);
    React.useEffect(() => {
      if (!serverSettings || seededFor.current === offeringUuid) return;
      seededFor.current = offeringUuid;
      if (typeof serverSettings.is_enabled === "boolean") {
        setMasterEnabled(serverSettings.is_enabled);
      }
      if (serverSettings.selection_method) {
        setSelectionMethod(
          mapSelectionMethodFromBackend(serverSettings.selection_method)
        );
      }
    }, [serverSettings, offeringUuid, setMasterEnabled, setSelectionMethod]);

    const handleOpen = () => {
      setOpen(true, offeringUuid || null);
    };

    const handleSave = ({ masterEnabled, selectionMethod }) => {
      if (!offeringUuid) return;
      saveSettings({
        listing_uid: offeringUuid,
        is_enabled: !!masterEnabled,
        selection_method: mapSelectionMethod(selectionMethod),
      });
    };

    const handleReset = () => {
      if (!offeringUuid) return;
      saveSettings({ listing_uid: offeringUuid, reset_to_account: true });
    };

    return (
      <div className="">
        <Button
          color="secondary"
          variant="outlined"
          size="small"
          className="!tw-h-[32px] !tw-text-[14px]"
          onClick={handleOpen}
        >
          <SettingsOutlined className="!tw-h-[18px] !tw-w-[18px] tw-mr-[6px]" />
          Offers & surge
        </Button>
        <OffersSurgeSettingsModal
          onSave={handleSave}
          onReset={handleReset}
          saving={isSaving}
        />
      </div>
    );
  }
);
